// src/data/eventsImportService.js
// Data layer for the Steward Events Importer (Phase 4) and the Pending Events
// publish view. Wraps the Phase 1 RPCs (`get_steward_freguesia_areas`,
// `import_event`), the Phase 3 Edge Function (`fetch-event-image`), and the
// plain `articles`/`event_details` queries the Pending Events view needs.
//
// RPC parameter names below must match DATABASE.md § RPC Functions § Steward
// Bulk Events Import exactly (`import_event`, 18 named params) — do not rename.

import { supabaseFetch } from '../lib/supabase';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

/**
 * The steward's expanded freguesia areas (already includes above-freguesia
 * appointments expanded to their freguesia children). No arguments — the
 * function derives the caller from auth.uid() (SECURITY INVOKER).
 * @returns {Promise<Array<{boundary_id: number, boundary_name: string, admin_level: number}>>}
 */
export async function getStewardFreguesiaAreas() {
  try {
    const { data, error } = await supabaseFetch.rpc('get_steward_freguesia_areas');
    if (error) {
      console.error('getStewardFreguesiaAreas error:', error);
      return [];
    }
    return data || [];
  } catch (err) {
    console.error('getStewardFreguesiaAreas exception:', err);
    return [];
  }
}

/**
 * Resolve a boundary's own name and its parent's name (for the locality/
 * municipality cross-check display). No nested joins — two separate queries.
 */
export async function getBoundaryWithParentName(boundaryId) {
  const { data: boundary, error } = await supabaseFetch.query('admin_boundaries', {
    select: 'id,name,parent_id',
    eq: { id: boundaryId },
    single: true
  });
  if (error || !boundary) return { name: null, parentName: null };

  let parentName = null;
  if (boundary.parent_id) {
    const { data: parent } = await supabaseFetch.query('admin_boundaries', {
      select: 'name',
      eq: { id: boundary.parent_id },
      single: true
    });
    parentName = parent?.name || null;
  }
  return { name: boundary.name, parentName };
}

/**
 * National (PT-wide) freguesia name search — used only to distinguish, for a
 * row whose locality matches nothing in the steward's granted set, between
 * "a real parish, just not yours" (hard-blocked) and "doesn't resolve at all"
 * (flagged, opt-in fallback to the batch freguesia). Phase 5b §2.3.
 *
 * Known limitation, cannot be closed client-side: `search_boundaries` matches
 * with plain `ILIKE`, which is accent-SENSITIVE — a source name transcribed
 * without the correct Portuguese accents will not match even a real freguesia.
 * Our own AI-instructions prompt requires "correct accents" (reduces the risk
 * in the common path), but hand-typed or copy-pasted source material could
 * still trip this. Closing it properly needs a server-side unaccent-aware
 * search, which is a migration — out of scope for this client-side-only phase.
 * Flagged in the task report, not silently swept under the rug.
 * @returns {Promise<Array<{id: number, name: string}>>}
 */
export async function searchFreguesiaNationally(searchTerm) {
  if (!searchTerm) return [];
  try {
    const { data, error } = await supabaseFetch.rpc('search_boundaries', {
      p_search_term: searchTerm,
      p_admin_level: 8,
      p_limit: 5
    });
    if (error) {
      console.error('searchFreguesiaNationally error:', error);
      return [];
    }
    return data || [];
  } catch (err) {
    console.error('searchFreguesiaNationally exception:', err);
    return [];
  }
}

/**
 * Existing (already-imported) events in the batch freguesia, for duplicate
 * detection. Only the fields findDuplicate() needs.
 */
export async function getExistingEventsForFreguesia(freguesiaId) {
  const { data, error } = await supabaseFetch.query('articles', {
    select: 'id,title',
    eq: { article_type: 'event', home_locality_id: freguesiaId }
  });
  if (error || !data) return [];

  const ids = data.map(a => a.id);
  if (ids.length === 0) return [];

  const { data: details } = await supabaseFetch.query('event_details', {
    select: 'article_id,start_date',
    inFilter: { article_id: ids }
  });
  const startDateByArticle = new Map((details || []).map(d => [d.article_id, d.start_date]));

  return data
    .filter(a => startDateByArticle.has(a.id))
    .map(a => ({ id: a.id, title: a.title, start_date: startDateByArticle.get(a.id) }));
}

// The request body field fetch-event-image reads (supabase/functions/fetch-event-image/index.ts,
// `body.url`). The two drifted once — from 5 Aug 2026 the importer sent `image_url` and every
// fetch was refused for two months (docs/briefs/2026-10-02-importer-images-arrive-again.md).
// src/data/__tests__/eventsImportService.test.js reads both files and fails if they diverge.
export const FETCH_EVENT_IMAGE_URL_FIELD = 'url';

// The function's HTTP status → a stable code the importer translates (importer.error_<code>).
// Its English error text is logged for developers only, never shown (TRANSLATION.md).
export const FETCH_EVENT_IMAGE_STATUS_CODES = {
  400: 'image_fetch_bad_address',
  401: 'image_fetch_not_allowed',
  403: 'image_fetch_not_allowed',
  413: 'image_fetch_too_large',
  429: 'image_fetch_rate_limited',
  502: 'image_fetch_unreachable',
  504: 'image_fetch_unreachable'
};

// 400s that are faults in the REQUEST this file builds, not in the steward's link — they must
// never be reported as "this web address doesn't lead to a picture" (that would have hidden the
// two-month drift above behind a message blaming the steward).
const REQUEST_FAULT_PREFIXES = ['Invalid JSON', 'Missing'];

/**
 * The stable code for a failed fetch-event-image response.
 * @param {number} status
 * @param {string} [serverError] - the function's own error text, if any
 * @returns {string}
 */
export function fetchEventImageErrorCode(status, serverError = '') {
  if (status === 400 && REQUEST_FAULT_PREFIXES.some(p => String(serverError).startsWith(p))) {
    return 'image_fetch_failed';
  }
  return FETCH_EVENT_IMAGE_STATUS_CODES[status] || 'image_fetch_failed';
}

/**
 * Call the Phase 3 Edge Function to fetch an image_url server-side (browsers
 * block cross-origin image fetches). Steward-authenticated only. Must only be
 * called for rows the steward has ticked the rights checkbox for — enforced
 * by the caller, not here.
 *
 * Note: this function's platform JWT gateway rejects the call with
 * UNAUTHORIZED_LEGACY_JWT unless the anon `apikey` header is ALSO sent
 * alongside `Authorization: Bearer <token>` (DATABASE.md § Edge Functions).
 * Throws a stable code (see fetchEventImageErrorCode), never server English.
 * @returns {Promise<Blob>}
 */
export async function fetchEventImageViaEdgeFunction(imageUrl) {
  const authToken = await supabaseFetch.getAuthToken();
  if (!authToken) {
    throw new Error('not_logged_in');
  }

  const response = await fetch(`${SUPABASE_URL}/functions/v1/fetch-event-image`, {
    method: 'POST',
    headers: {
      'apikey': SUPABASE_ANON_KEY,
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ [FETCH_EVENT_IMAGE_URL_FIELD]: imageUrl })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Diagnostic detail logged for developers; the thrown error is a stable
    // code the UI translates via t() — TRANSLATION.md forbids surfacing raw
    // English (including server-returned English) directly to the steward.
    console.error('fetchEventImageViaEdgeFunction failed:', response.status, errorData);
    throw new Error(fetchEventImageErrorCode(response.status, errorData?.error));
  }

  return response.blob();
}

/**
 * Create one event via the atomic import_event RPC. Always is_published=false
 * (set by the RPC, not passed here) — this page never flips it.
 * @param {object} p - matches import_event's SQL parameter names 1:1
 * @returns {Promise<string>} the new articles.id
 */
export async function importEvent(p) {
  const { data, error } = await supabaseFetch.rpc('import_event', {
    p_author_id: p.authorId,
    p_freguesia_id: p.freguesiaId,
    p_title: p.title,
    p_description: p.description,
    p_start_date: p.startDate,
    p_summary: p.summary ?? null,
    p_end_date: p.endDate ?? null,
    p_is_all_day: p.isAllDay ?? false,
    p_venue_name: p.venueName ?? null,
    p_venue_address: p.venueAddress ?? null,
    p_venue_postcode: p.venuePostcode ?? null,
    p_price_info: p.priceInfo ?? null,
    p_requires_rsvp: p.requiresRsvp ?? false,
    p_max_attendees: p.maxAttendees ?? null,
    p_contact_email: p.contactEmail ?? null,
    p_contact_phone: p.contactPhone ?? null,
    p_event_url: p.eventUrl ?? null,
    p_tags: p.tags ?? null
  });
  if (error) {
    console.error('import_event failed:', error);
    throw new Error('import_event_failed');
  }
  return data;
}

/**
 * Set the image on a newly-imported event (a steward-supplied photo, fetched
 * from the CSV's image_url — takes precedence over any tag-default image,
 * R5). import_event() does not set this itself (DATABASE.md:1557).
 *
 * Writes ONLY cover_image_url — the canonical event-image column (image-layer
 * build, docs/briefs/2026-08-11-image-layer-build.md §2). Used to also write
 * hero_image_display_url/hero_image_thumbnail_url; those are now vestigial
 * for events (§2: "left in place, not populated, not read", same disposition
 * as article_tags under R8) since every render surface and the Pending list
 * both read cover_image_url — populating the hero_image_* pair here would
 * just be adding a fourth stale copy.
 */
export async function setEventImage(articleId, { displayUrl }) {
  const authToken = await supabaseFetch.getAuthToken();
  const { error } = await supabaseFetch.update('articles', {
    cover_image_url: displayUrl
  }, { eq: { id: articleId }, authToken });
  if (error) {
    console.error('setEventImage failed:', error);
    throw new Error('image_attach_failed');
  }
}

/**
 * Set the tag-illustration image on an event (image-layer build §4) — the
 * matcher's follow-up write. Mirrors setEventTagSlugs()'s shape exactly
 * (non-throwing: a matcher-write failure must not turn an already-succeeded
 * import into a reported failure, same reasoning as Addendum 1 guardrail 2).
 * Caller is responsible for R5 precedence (never call this over a steward's
 * own photo) and for skipping the call when there's no image to write.
 * @returns {Promise<{error: object|null}>}
 */
export async function setEventTagImage(articleId, imageUrl) {
  try {
    const authToken = await supabaseFetch.getAuthToken();
    const { error } = await supabaseFetch.update('articles', {
      cover_image_url: imageUrl
    }, { eq: { id: articleId }, authToken });
    if (error) {
      console.error('setEventTagImage failed:', error);
      return { error };
    }
    return { error: null };
  } catch (err) {
    console.error('setEventTagImage exception:', err);
    return { error: err };
  }
}

/**
 * Set the ratified tag slugs on a newly-imported event. import_event() writes
 * the CSV's raw `tags` value into the legacy `article_tags` column, not
 * `tag_slugs` (docs/escalations/2026-08-11-simple-importer-tag-write-path.md) —
 * this is the front-end follow-up write that closes that gap, ruled in
 * docs/briefs/2026-08-11-simple-importer-build-addendum-1.md. Mirrors
 * setEventImage()'s shape (a second update after import_event() returns), but
 * deliberately does NOT throw: a tag-attach failure must not turn an
 * already-succeeded import into a reported failure (Addendum 1 guardrail 2 —
 * "the event stays imported (untagged) — surface/log it, do not... throw in a
 * way that loses the event"). Caller is expected to skip the call entirely
 * when `slugs` is empty, matching AddEditEvent.jsx's tag_slugs convention.
 * @returns {Promise<{error: object|null}>}
 */
export async function setEventTagSlugs(articleId, slugs) {
  try {
    const authToken = await supabaseFetch.getAuthToken();
    const { error } = await supabaseFetch.update('articles', {
      tag_slugs: slugs
    }, { eq: { id: articleId }, authToken });
    if (error) {
      console.error('setEventTagSlugs failed:', error);
      return { error };
    }
    return { error: null };
  } catch (err) {
    console.error('setEventTagSlugs exception:', err);
    return { error: err };
  }
}

/**
 * The current steward's own pending (unpublished) imported events, newest
 * first, for the Pending Events view. Scoped by authorship only (§6.2) — not
 * by freguesia or get_steward_freguesia_areas().
 */
export async function getPendingEvents(userId) {
  const { data: articles, error } = await supabaseFetch.query('articles', {
    select: 'id,title,cover_image_url,home_locality_id,created_at',
    eq: { article_type: 'event', is_published: false, author_id: userId },
    order: 'created_at.desc'
  });
  if (error || !articles || articles.length === 0) return [];

  const articleIds = articles.map(a => a.id);
  const { data: details } = await supabaseFetch.query('event_details', {
    select: 'article_id,start_date',
    inFilter: { article_id: articleIds }
  });
  const startDateByArticle = new Map((details || []).map(d => [d.article_id, d.start_date]));

  const boundaryIds = [...new Set(articles.map(a => a.home_locality_id).filter(Boolean))];
  const { data: boundaries } = boundaryIds.length
    ? await supabaseFetch.query('admin_boundaries', { select: 'id,name', inFilter: { id: boundaryIds } })
    : { data: [] };
  const boundaryNameById = new Map((boundaries || []).map(b => [b.id, b.name]));

  return articles.map(a => ({
    id: a.id,
    title: a.title,
    thumbnailUrl: a.cover_image_url,
    startDate: startDateByArticle.get(a.id) || null,
    freguesiaName: boundaryNameById.get(a.home_locality_id) || null,
    importedAt: a.created_at
  }));
}

/**
 * Full article row for a single event, for the Pending Events "Edit" action —
 * AddEditEvent's edit mode needs the whole row (it fetches event_details
 * itself by article_id), not the thin projection getPendingEvents() lists with.
 */
export async function getEventArticleById(articleId) {
  const { data, error } = await supabaseFetch.query('articles', {
    select: '*',
    eq: { id: articleId },
    single: true
  });
  if (error) {
    console.error('getEventArticleById failed:', error);
    throw new Error('event_load_failed');
  }
  return data;
}

/**
 * Flip is_published=true on the given event ids (through supabaseFetch,
 * authenticated — see task report for why not the brief's named
 * `protectedSupabase`, which is a dead pass-through with no bulk API).
 * published_at is not sent: the database dates a post at its first publication
 * and never moves it (trigger set_article_published_at, migration 20260928i;
 * #78 addendum 5), and an import no longer carries a date from the import.
 */
export async function publishEvents(articleIds) {
  const authToken = await supabaseFetch.getAuthToken();
  const results = await Promise.all(articleIds.map(id =>
    supabaseFetch.update('articles',
      { is_published: true },
      { eq: { id }, authToken }
    )
  ));
  const failed = results
    .map((r, i) => ({ id: articleIds[i], error: r.error }))
    .filter(x => x.error);
  return { succeededCount: articleIds.length - failed.length, failed };
}
